/**
 * How a picture looks, in a few numbers (Huanrui, 2026-10-15)
 * -------------------------------------------------------------
 * The visual check on what comes into the libraries. Two pictures that look
 * the same -- one of them recompressed, resized, re-saved, or sent through
 * Miro and back -- get nearly the same numbers, though their bytes differ
 * and their file hashes have nothing in common.
 *
 *   shape   a 64-bit perceptual hash: the picture shrunk to 32 x 32 in grey,
 *           its broad pattern of light and dark (the lowest 8 x 8 of its
 *           cosine transform), each bit above or below the middle value.
 *           Pictures are compared by how many of the 64 bits differ.
 *   colour  the average colour of each ninth of the picture, 3 x 3. The
 *           shape alone cannot tell two flavours of one pack apart; this can.
 *   detail  the 32 x 32 grey itself, one byte a place (1,024 bytes, as hex).
 *           The second check (2026-10-15): the shape is only the broad
 *           pattern, and the library is full of design variants -- one bottle
 *           with another label, pattern or texture -- that share it exactly.
 *           A real copy (re-saved, resized, through Miro and back) matches
 *           here place by place; a variant differs where its label does.
 *           Compared by detailApart: the average difference, and the worst of
 *           the 64 blocks of 4 x 4 places, so a change in one corner counts.
 *
 * Plain JavaScript on purpose, with no browser or Node in it: the Miro panel
 * (canvas), the Egnyte importer and the backfill (sharp) each shrink the
 * picture to about 256 pixels and hand over its RGBA; everything after that
 * is this one file, so all three get the same answer for the same picture.
 * Types: signature.d.ts.
 */

const SIZE = 32
const KEEP = 8

/** cos((2x + 1) u pi / 64), the transform's table, made once. */
const COS = (() => {
  const table = new Float64Array(SIZE * SIZE)
  for (let u = 0; u < SIZE; u += 1) {
    for (let x = 0; x < SIZE; x += 1) table[u * SIZE + x] = Math.cos(((2 * x + 1) * u * Math.PI) / (2 * SIZE))
  }
  return table
})()

/**
 * The picture's look, from its pixels: `rgba` is width x height x 4, any
 * size (about 256 on the longer side is plenty). See-through parts are put
 * on white first, as the library stores them.
 *
 * @param {Uint8Array | Uint8ClampedArray} rgba
 * @param {number} width
 * @param {number} height
 * @returns {{ hash: string, hashSigned: string, colour: number[], detail: string }}
 */
export function lookOf(rgba, width, height) {
  if (!(width > 0 && height > 0) || rgba.length < width * height * 4) throw new Error('No pixels to read')
  const grey = new Float64Array(SIZE * SIZE)
  const count = new Float64Array(SIZE * SIZE)
  const cells = new Float64Array(9 * 3)
  const cellCount = new Float64Array(9)
  for (let y = 0; y < height; y += 1) {
    const gy = Math.min(SIZE - 1, Math.floor((y * SIZE) / height))
    const cy = Math.min(2, Math.floor((y * 3) / height))
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4
      const a = rgba[i + 3] / 255
      const r = rgba[i] * a + 255 * (1 - a)
      const g = rgba[i + 1] * a + 255 * (1 - a)
      const b = rgba[i + 2] * a + 255 * (1 - a)
      const gx = Math.min(SIZE - 1, Math.floor((x * SIZE) / width))
      grey[gy * SIZE + gx] += 0.299 * r + 0.587 * g + 0.114 * b
      count[gy * SIZE + gx] += 1
      const c = cy * 3 + Math.min(2, Math.floor((x * 3) / width))
      cells[c * 3] += r
      cells[c * 3 + 1] += g
      cells[c * 3 + 2] += b
      cellCount[c] += 1
    }
  }
  // A picture smaller than 32 leaves empty places: each takes its nearest neighbour.
  for (let gy = 0; gy < SIZE; gy += 1) {
    for (let gx = 0; gx < SIZE; gx += 1) {
      const k = gy * SIZE + gx
      if (count[k] > 0) grey[k] /= count[k]
      else {
        const sx = Math.min(width - 1, Math.floor(((gx + 0.5) * width) / SIZE))
        const sy = Math.min(height - 1, Math.floor(((gy + 0.5) * height) / SIZE))
        const i = (sy * width + sx) * 4
        const a = rgba[i + 3] / 255
        grey[k] =
          0.299 * (rgba[i] * a + 255 * (1 - a)) + 0.587 * (rgba[i + 1] * a + 255 * (1 - a)) + 0.114 * (rgba[i + 2] * a + 255 * (1 - a))
      }
    }
  }

  // The lowest 8 x 8 of the 2-D cosine transform.
  const rows = new Float64Array(KEEP * SIZE)
  for (let u = 0; u < KEEP; u += 1) {
    for (let y = 0; y < SIZE; y += 1) {
      let sum = 0
      for (let x = 0; x < SIZE; x += 1) sum += grey[y * SIZE + x] * COS[u * SIZE + x]
      rows[u * SIZE + y] = sum
    }
  }
  const coeffs = new Float64Array(KEEP * KEEP)
  for (let v = 0; v < KEEP; v += 1) {
    for (let u = 0; u < KEEP; u += 1) {
      let sum = 0
      for (let y = 0; y < SIZE; y += 1) sum += rows[u * SIZE + y] * COS[v * SIZE + y]
      coeffs[v * KEEP + u] = sum
    }
  }
  // Above or below the middle value, the overall brightness (the first) left out of the middle.
  const sorted = Array.from(coeffs.subarray(1)).sort((p, q) => p - q)
  const middle = (sorted[31] + sorted[32]) / 2
  let bits = 0n
  for (let k = 0; k < 64; k += 1) bits = (bits << 1n) | (coeffs[k] > middle ? 1n : 0n)
  const hash = bits.toString(16).padStart(16, '0')

  const colour = []
  for (let c = 0; c < 9; c += 1) {
    const n = Math.max(1, cellCount[c])
    colour.push(Math.round(cells[c * 3] / n), Math.round(cells[c * 3 + 1] / n), Math.round(cells[c * 3 + 2] / n))
  }
  let detail = ''
  for (let k = 0; k < SIZE * SIZE; k += 1) detail += Math.max(0, Math.min(255, Math.round(grey[k]))).toString(16).padStart(2, '0')
  return { hash, hashSigned: BigInt.asIntN(64, bits).toString(), colour, detail }
}

/**
 * How far apart two details are (0 to 255 each): `mean`, the average
 * difference over the 1,024 places; `worst`, the largest average difference
 * of any one of the 64 blocks of 4 x 4 places. The same sum as the database's
 * detail_apart (docs/visual-duplicates-detail.sql).
 *
 * @param {string} a  2,048 hex digits
 * @param {string} b  2,048 hex digits
 * @returns {{ mean: number, worst: number }}
 */
export function detailApart(a, b) {
  const blocks = new Float64Array(64)
  let sum = 0
  for (let k = 0; k < SIZE * SIZE; k += 1) {
    const d = Math.abs(parseInt(a.slice(k * 2, k * 2 + 2), 16) - parseInt(b.slice(k * 2, k * 2 + 2), 16))
    sum += d
    const y = Math.floor(k / SIZE)
    const x = k % SIZE
    blocks[Math.floor(y / 4) * 8 + Math.floor(x / 4)] += d
  }
  let worst = 0
  for (let k = 0; k < 64; k += 1) worst = Math.max(worst, blocks[k] / 16)
  return { mean: sum / (SIZE * SIZE), worst }
}

/**
 * How many of the 64 bits two shapes differ in: 0 the same, about 32 for
 * pictures with nothing in common.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function shapeDistance(a, b) {
  let x = BigInt('0x' + a) ^ BigInt('0x' + b)
  let n = 0
  while (x > 0n) {
    n += Number(x & 1n)
    x >>= 1n
  }
  return n
}

/**
 * How far apart two colour grids are: the average, over the nine places, of
 * the straight-line distance between their colours (0 to about 441).
 *
 * @param {number[]} a
 * @param {number[]} b
 * @returns {number}
 */
export function colourGap(a, b) {
  let sum = 0
  for (let c = 0; c < 9; c += 1) {
    const dr = a[c * 3] - b[c * 3]
    const dg = a[c * 3 + 1] - b[c * 3 + 1]
    const db = a[c * 3 + 2] - b[c * 3 + 2]
    sum += Math.sqrt(dr * dr + dg * dg + db * db)
  }
  return sum / 9
}

/**
 * The size to shrink a picture to before reading it: the longer side 256,
 * never enlarged.
 *
 * @param {number} width
 * @param {number} height
 * @returns {{ width: number, height: number }}
 */
export function readingSize(width, height) {
  const k = Math.min(1, 256 / Math.max(width, height, 1))
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) }
}
